import { call, put, takeEvery, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { operationTypeApi, OperationType, OperationTypeSearchParams } from '../../services/operationTypeApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  FETCH_OPERATION_TYPES,
  FETCH_OPERATION_TYPE_BY_ID,
  CREATE_OPERATION_TYPE,
  UPDATE_OPERATION_TYPE,
  DELETE_OPERATION_TYPE,
  SEARCH_OPERATION_TYPES,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/operationTypeSlice';

// Fetch all Operation Types
function* fetchOperationTypesSaga(action: PayloadAction<OperationTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { pagination, searchParams } = state.operationType;
    
    const params = {
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: searchParams.search || '',
      active: searchParams.active,
      sortBy: searchParams.sortBy || 'Name',
      sortOrder: searchParams.sortOrder || 'asc',
      ...action.payload,
    };
    
    const response = yield call(operationTypeApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Operation Types:', error);
    yield put(setError(error.message || 'Failed to fetch Operation Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch Operation Type by ID
function* fetchOperationTypeByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const operationType = yield call(operationTypeApi.getById, action.payload);
    yield put(setSelectedItem(operationType));
  } catch (error: any) {
    console.error('Error fetching Operation Type by ID:', error);
    yield put(setError(error.message || 'Failed to fetch Operation Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Create Operation Type
function* createOperationTypeSaga(action: PayloadAction<Omit<OperationType, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(operationTypeApi.validation.validateOperationType, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newOperationType = yield call(operationTypeApi.create, action.payload);
    yield put(addItem(newOperationType));
    yield put(setSuccessMessage('Operation Type created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Operation Type:', error);
    yield put(setError(error.message || 'Failed to create Operation Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Update Operation Type
function* updateOperationTypeSaga(action: PayloadAction<{ id: number; operationType: Partial<OperationType> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, operationType } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(operationTypeApi.validation.validateOperationType, operationType);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedOperationType = yield call(operationTypeApi.update, id, operationType);
    yield put(updateItem(updatedOperationType));
    yield put(setSuccessMessage('Operation Type updated successfully'));
  } catch (error: any) {
    console.error('Error updating Operation Type:', error);
    yield put(setError(error.message || 'Failed to update Operation Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Delete Operation Type
function* deleteOperationTypeSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(operationTypeApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Operation Type deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Operation Type'));
    }
  } catch (error: any) {
    console.error('Error deleting Operation Type:', error);
    yield put(setError(error.message || 'Failed to delete Operation Type'));
  } finally {
    yield put(setLoading(false));
  }
}

// Search Operation Types
function* searchOperationTypesSaga(action: PayloadAction<OperationTypeSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(operationTypeApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Operation Types:', error);
    yield put(setError(error.message || 'Failed to search Operation Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch statistics
function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(operationTypeApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

// Bulk activate
function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(operationTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(operationTypeApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Operation Types activated successfully'));
      // Refresh the list
      yield put({ type: FETCH_OPERATION_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to activate Operation Types'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Operation Types:', error);
    yield put(setError(error.message || 'Failed to activate Operation Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk deactivate
function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(operationTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(operationTypeApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Operation Types deactivated successfully'));
      // Refresh the list
      yield put({ type: FETCH_OPERATION_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Operation Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Operation Types:', error);
    yield put(setError(error.message || 'Failed to deactivate Operation Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Bulk delete
function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(operationTypeApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors[0]));
      return;
    }
    
    const response = yield call(operationTypeApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(response.message || 'Operation Types deleted successfully'));
      // Refresh the list
      yield put({ type: FETCH_OPERATION_TYPES });
    } else {
      yield put(setError(response.message || 'Failed to delete Operation Types'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Operation Types:', error);
    yield put(setError(error.message || 'Failed to delete Operation Types'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

// Export to CSV
function* exportToCSVSaga(action: PayloadAction<OperationTypeSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const state: RootState = yield select();
    const { searchParams } = state.operationType;
    
    const params = {
      ...searchParams,
      ...action.payload,
    };
    
    const blob = yield call(operationTypeApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `operationtypes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Operation Types exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Operation Types:', error);
    yield put(setError(error.message || 'Failed to export Operation Types'));
  } finally {
    yield put(setLoading(false));
  }
}

// Root Operation Type Saga
export function* operationTypeSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_OPERATION_TYPES, fetchOperationTypesSaga);
  yield takeEvery(FETCH_OPERATION_TYPE_BY_ID, fetchOperationTypeByIdSaga);
  yield takeEvery(CREATE_OPERATION_TYPE, createOperationTypeSaga);
  yield takeEvery(UPDATE_OPERATION_TYPE, updateOperationTypeSaga);
  yield takeEvery(DELETE_OPERATION_TYPE, deleteOperationTypeSaga);
  yield takeEvery(SEARCH_OPERATION_TYPES, searchOperationTypesSaga);
  yield takeEvery(FETCH_STATISTICS, fetchStatisticsSaga);
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
} 